<script setup lang="ts">
import WithdrawalOperation from "@models/operations/WithdrawalOperation";
import {P_WITHDRAWAL_STATUS} from "@/entities/enums/PWithdrawalStatus";

interface Props {
	withdrawal: WithdrawalOperation
}

interface Emits {
	(e: 'cancelPayment'): void;
	(e: 'openRules'): void;
}

defineProps<Props>();
const emits = defineEmits<Emits>();

const handleCancelWithdrawal = () => {
	emits('cancelPayment');
}

const handleGoRules = () => {
	emits('openRules');
}
</script>

<template>
    <div class="withdrawal-faq">
        <f7-block-title>FAQ</f7-block-title>
        <f7-list strong dividers-ios inset accordion-list>
            <f7-list-item accordion-item :title="$t('withdrawal.faq.how-does-agent-search-work.title')">
                <f7-accordion-content>
                    <f7-block>
                        <p>{{ $t('withdrawal.faq.how-does-agent-search-work.description') }}</p>
                    </f7-block>
                </f7-accordion-content>
            </f7-list-item>
            <f7-list-item accordion-item :title="$t('withdrawal.faq.received-funds.title')">
                <f7-accordion-content>
                    <f7-block>
                        <p>
                            {{ $t('withdrawal.faq.received-funds.description') }}
                            <span class="rules" @click="handleGoRules">{{ $t('withdrawal.faq.received-funds.link') }}</span>
                        </p>
                    </f7-block>
                </f7-accordion-content>
            </f7-list-item>
            <f7-list-item accordion-item :title="$t('withdrawal.faq.cancel-order.title')">
                <f7-accordion-content>
                    <f7-block>
                        <template v-if="withdrawal.status === P_WITHDRAWAL_STATUS.CREATED">
                            <p>{{ $t('withdrawal.faq.cancel-order.statuses.created.description') }}</p>
                            <span class="cancel-order" @click="handleCancelWithdrawal">
                                {{ $t('withdrawal.faq.cancel-order.statuses.created.button') }}
                            </span>
                        </template>
                        <template v-else>
                            <p>{{ $t('withdrawal.faq.cancel-order.statuses.accepted.description') }}</p>
                        </template>
                    </f7-block>
                </f7-accordion-content>
            </f7-list-item>
        </f7-list>
    </div>
</template>

<style scoped lang="scss">
.withdrawal-faq {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .block-title {
    color: var(--grey2);
    margin: 0 var(--f7-list-inset-side-margin);
  }

  .accordion-list {
    margin: 0;
    border: 1px solid var(--grey8);

    &:deep(ul) {
      background-color: var(--grey9);
    }

    &:deep(.accordion-item) {
      .item-link {
        .item-inner {

          .item-title {
            color: var(--brown1);
            font-size: 16px;
            line-height: 120%;
          }

          &:after {
            background-color: var(--grey8);
            width: calc(100% - (var(--f7-list-item-padding-horizontal) + var(--f7-safe-area-left)));
          }
        }
      }

      &.accordion-item-opened {
        .item-link {
          .item-inner {
            &:after {
              background-color: transparent;
            }
          }
        }
      }

      .accordion-item-content {
        .block {
          padding-top: 4px;
          padding-bottom: 8px;

          & > * {
            font-size: 14px;
            line-height: 120%;
            color: var(--brown1);
            margin: 10px 0;

            &:first-child {
              margin-top: 0 !important;
            }

            &:last-child {
              margin-bottom: 0 !important;
            }
          }

          .cancel-order, .rules {
            cursor: pointer;
            font-weight: 500;
            text-decoration-line: underline;
            text-underline-offset: 4px;
          }

          .cancel-order {
            color: var(--red1);
          }

          .rules {
            color: var(--blue1);
          }
        }
      }
    }
  }
}
</style>
